import { useQuery } from '@apollo/client';
import ParkingContext from 'context/ParkingContext';
import { format, isBefore } from 'date-fns';
import { READ_PARKING_SESSIONS } from 'graphql/queries/readParkingSessions';
import useParkingHistoryFilter from 'hooks/useParkingHistoryFilter';
import { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParkzoneStore } from 'stores/parkzoneStore';
import {
  CountryCode,
  Maybe,
  ParkingSessionType,
  ParkingState,
  PredefinedTicket,
  Query,
  QueryReadParkingSessionsArgs,
  SessionType,
  Vehicle,
} from 'types/generatedSchemaTypes';
import { isCompletedPaymentState } from 'utils/paymentUtils';
import { getTimeLeft } from 'utils/timeUtils';
import useIsLoggedIn from './useIsLoggedIn';

interface PrepaidInputParams {
  fetchAll: boolean;
  commercialZoneCode?: Maybe<string>;
  country?: Maybe<CountryCode>;
  licensePlateText?: Maybe<string>;
}

type ReadPurchasedPrepaidTicketsType = {
  fetchAll?: boolean;
  selectedVehicle?: Vehicle;
};

const getPrepaidTicketsWithCorporateAccount = (
  data?: Pick<Query, 'readParkingSessions'>
) => {
  const sessions = data?.readParkingSessions?.content ?? [];
  return sessions.filter(
    (parkingSession) => parkingSession?.paymentType === 'CORPORATE_ACCOUNT'
  );
};

const formatPrepaidTicketName = (
  startDate: string,
  endDate: string,
  t: (key: string, options?: any) => string
): string => {
  const startDateInFuture = isBefore(new Date(), new Date(startDate));
  if (startDateInFuture) {
    const formatedDate = format(new Date(startDate), 'd.M.yyyy HH:mm');
    return `${t('parking.prepaidTickets.validFrom', { date: formatedDate })}`;
  }
  const { timeLeft } = getTimeLeft(new Date(endDate), new Date(), true);
  return `${t('parking.prepaidTickets.validFor')} ${timeLeft}`;
};

const mapToPredefinedTicket = (
  parkingSession: any,
  t: (key: string, options?: any) => string
): PredefinedTicket | undefined => {
  if (!parkingSession) return undefined;

  return {
    sessionType: parkingSession.type as SessionType,
    sessionId: parkingSession.sessionId,
    type: 'FIXED_DURATION',
    price: parkingSession.price?.totalAmount,
    startTime: parkingSession.startDate?.toString() as string,
    parkZoneName:
      parkingSession?.parkZone?.commercialZoneView?.name ||
      parkingSession?.parkZone?.name,
    endTime: parkingSession.predefinedEndDate,
    name: formatPrepaidTicketName(
      parkingSession.startDate?.toString() as string,
      parkingSession.predefinedEndDate as string,
      t
    ),
    accessDevice: parkingSession.accessDevice,
    reassignments: parkingSession.reassignments,
    customerSegmentUid: parkingSession.customerSegment?.uid,
  };
};

export const createPrepaidInput = ({
  fetchAll,
  commercialZoneCode,
  country,
  licensePlateText,
}: PrepaidInputParams) => {
  return {
    input: {
      parkingState: ['Started', 'NotStarted'] as ParkingState[],
      sessionType: ['PREPAID'] as ParkingSessionType[],
      zoneCode: !fetchAll ? commercialZoneCode : undefined,
      countryCode: !fetchAll ? country : undefined,
      accessDeviceValue: licensePlateText,
    },
  };
};

const useReadPurchasedPrepaidTickets = ({
  fetchAll = false,
  selectedVehicle,
}: ReadPurchasedPrepaidTicketsType) => {
  const { isLoggedIn } = useIsLoggedIn();
  const { t } = useTranslation();
  const { parkingZoneResult } = useContext(ParkingContext);
  const selectedCustomerSegmentUid = useParkzoneStore(
    (state) => state.selectedTariffId
  );
  const { vehicle: vehicleFilter } = useParkingHistoryFilter();

  const commercialZoneCode =
    parkingZoneResult?.data?.parkZone?.commercialZoneView?.zoneCode;
  const country = parkingZoneResult?.data?.parkZone?.countryCode;

  const {
    data,
    loading,
    error,
    refetch: refetchPrepaidTickets,
  } = useQuery<Query, QueryReadParkingSessionsArgs>(READ_PARKING_SESSIONS, {
    variables: createPrepaidInput({
      fetchAll,
      commercialZoneCode,
      country,
      licensePlateText: vehicleFilter?.licensePlate?.plateText,
    }),
    skip: (!fetchAll && !commercialZoneCode) || !isLoggedIn,
    fetchPolicy: 'cache-and-network',
    errorPolicy: 'all',
  });

  const purchasedPrepaidTickets = useMemo(() => {
    const sessions = data?.readParkingSessions?.content ?? [];
    return sessions
      .filter((session) => isCompletedPaymentState(session?.paymentState))
      .map((session) => mapToPredefinedTicket(session, t));
  }, [data, t]);

  const prepaidTicketsWithCorporateAccount = useMemo(
    () => getPrepaidTicketsWithCorporateAccount(data),
    [data]
  );

  const hasPrepaidForSelectedVehicle = useMemo(
    () =>
      purchasedPrepaidTickets
        .filter((item): item is PredefinedTicket => !!item)
        .filter(
          ({ accessDevice, startTime, customerSegmentUid }) =>
            accessDevice?.value === selectedVehicle?.licensePlate.plateText &&
            customerSegmentUid === selectedCustomerSegmentUid &&
            startTime &&
            isBefore(new Date(startTime), new Date())
        ),
    [
      purchasedPrepaidTickets,
      selectedVehicle?.licensePlate.plateText,
      selectedCustomerSegmentUid,
    ]
  );

  return {
    data,
    purchasedPrepaidTickets,
    prepaidTicketsWithCorporateAccount,
    hasPrepaidForSelectedVehicle,
    loading,
    error,
    refetchPrepaidTickets,
  };
};

export default useReadPurchasedPrepaidTickets;
